/**
 * @fileoverview One bed, opened up, from the grid or the list (B157): the bed's twin of PlantQuickLook.
 *
 * A tile or a row is a bed in less space, so a tap gives back the space: the same card the Cards density draws
 * (LocationCard), in a dialog, not the Edit form. Edit details, Add a new plant here, Note for every plant and Clear the
 * bed are on the card; each closes this pop-up first and then opens its own sheet, so two dialogs are never stacked
 * (as PlantQuickLook.openEdit does). A plant tapped in the bed closes the bed's card, then opens the plant's.
 *
 * The card sizes itself to a grid column; in here the dialog is the column, so it fills. Its head row (`data-card-head`)
 * stops short of the dialog's close X, which would otherwise sit on top of the card's menu button.
 *
 * Where: LocationGrid (a tile or a row tapped). Not the archive page (a retired bed has no card to open).
 * API: none (the bed and its jobs are the page's own).
 * Settings: none.
 * Words (en/es): none of its own (the card's); the bed's name is the dialog's title for a screen reader.
 * Watch out: the bed is read from the page's list by the caller, so a photo or note added in here shows at once.
 *
 * @module BedQuickLook
 */

import React from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import type { Location } from '@/shared/api/locations';
import type { TodoMarks } from '@/features/today/lib/useTodoMarks';
import type { BriefBed, BriefTodo } from '@/shared/api/todayBrief';
import { LocationCard } from './LocationCard';

/** Which bed is open, how to close it, and what its card opens. */
interface BedQuickLookProps {
  /** The bed to open, or null when nothing is open. */
  location: Location | null;
  /** Closes the pop-up. */
  onClose: () => void;
  /** Opens Edit bed on it (after the pop-up closes). */
  onEdit: (location: Location) => void;
  /** Opens one of its plants, full size (after the pop-up closes). */
  onOpenPlant: (plantObjId: number) => void;
  /** Opens the Plants pop-up with this bed preset (after the pop-up closes). */
  onAddPlant?: () => void;
  /** Opens "A note for every plant" (after the pop-up closes). */
  onNoteAll?: () => void;
  /** Opens "Clear the bed for the season" (after the pop-up closes). */
  onClearAll?: () => void;
  /** The one to-do list, for the bed's This week; none without a brief. */
  todo?: BriefTodo | null;
  /** The bed's place in it. */
  bed?: BriefBed | null;
  /** Ticking, from the page's useTodoMarks. */
  marks?: TodoMarks;
  /** This week shows so many jobs, then "2 more jobs". */
  jobsShown?: number;
}

/**
 * The bed's card in a dialog.
 *
 * @param props - BedQuickLookProps.
 */
export const BedQuickLook: React.FC<BedQuickLookProps> = ({
  location, onClose, onEdit, onOpenPlant, onAddPlant, onNoteAll, onClearAll, todo, bed, marks, jobsShown,
}) => {
  /**
   * Wraps something the card opens so the pop-up closes first: two stacked dialogs is a worse answer than one.
   *
   * @param next - What to open once this is closed.
   */
  const closeThen = <A extends unknown[]>(next: (...args: A) => void) => (...args: A): void => {
    onClose();
    next(...args);
  };

  if (!location) return null;
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent scrollBody={false} className="flex max-h-[92dvh] w-[calc(100%-1.5rem)] flex-col overflow-hidden p-0 sm:max-w-md">
        {/* The card carries its own name, so the dialog's title would say it twice; a screen reader still needs one. */}
        <DialogTitle className="sr-only">{location.name}</DialogTitle>
        <div className="flex-1 overflow-y-auto">
          {/* The card fills the dialog, and its head row stops short of the close X. */}
          <div className="[&>div]:max-w-none [&>div]:rounded-none [&>div]:border-0 [&>div]:shadow-none [&_[data-card-head]]:pr-12">
            <LocationCard
              location={location}
              onEdit={closeThen(onEdit)}
              onOpenPlant={closeThen(onOpenPlant)}
              onAddPlant={onAddPlant && closeThen(onAddPlant)}
              onNoteAll={onNoteAll && closeThen(onNoteAll)}
              onClearAll={onClearAll && closeThen(onClearAll)}
              todo={todo}
              bed={bed}
              marks={marks}
              jobsShown={jobsShown}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
